import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';

import { useLogEntryStore } from '@/stores/log-entry-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { usePropertyStore } from '@/stores/property-store';
import { useActivePropertyStore } from '@/stores/active-property-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/common/Button';
import { InlineErrorBanner } from '@/components/common/InlineErrorBanner';
import { TagSelector } from '@/components/scouting/TagSelector';
import { PhotoPicker } from '@/components/photos/PhotoPicker';
import { PhotoGallery } from '@/components/photos/PhotoGallery';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { DateField } from '@/components/forms/DateField';
import { TimeField } from '@/components/forms/TimeField';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { getPhotosForEntry, addPhotosToEntry, deletePhoto } from '@/services/photo-service';
import { posthogService } from '@/services/posthog-service';
import { UPGRADE_PROMPT_SHOWN } from '@/services/observability/events';
import { ANIMAL_SEX_OPTIONS, ANIMAL_BEHAVIOR_OPTIONS } from '@/utils/scouting-utils';
import type { PhotoRecord } from '@/types/photo';
import { localTodayISO } from '@/utils/date-utils';

// M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
const INPUT_CLASS =
  'w-full px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-placeholder)] focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px]';

interface FieldLabelProps {
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}

function FieldLabel({ htmlFor, children, required }: FieldLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className="field-label"
    >
      {children}
      {required && <span className="text-[var(--color-accent)] ml-1">*</span>}
    </label>
  );
}

function FieldWrapper({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>;
}

export function LogEntryFormPage() {
  const { waypointId: waypointIdParam, entryId } = useParams<{ waypointId?: string; entryId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!entryId;
  const isPro = useUserProfileStore((s) => s.isPro);

  const userId = useAuthStore((s) => s.user?.id);
  const { entries, loadEntriesByWaypoint, createEntry, updateEntry } = useLogEntryStore();
  const { waypoints: locations, loadWaypoints } = useWaypointStore();
  const { properties, loadProperties } = usePropertyStore();
  const { activePropertyId } = useActivePropertyStore();

  // waypointId may come from path param (:waypointId/log/new) or ?location= query param (/scout/log/new)
  const waypointIdFromUrl = waypointIdParam ?? searchParams.get('location') ?? undefined;
  // When no URL waypointId, the user picks property then location from selectors
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(activePropertyId ?? '');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const waypointId = waypointIdFromUrl ?? (selectedLocationId || undefined);

  const today = localTodayISO();
  const nowTime = new Date().toTimeString().slice(0, 5);

  const [date, setDate] = useState(today);
  const [time, setTime] = useState(nowTime);
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [animalObserved, setAnimalObserved] = useState(false);
  const [animalSpecies, setAnimalSpecies] = useState('Whitetail');
  const [animalSex, setAnimalSex] = useState('');
  const [animalAgeClass, setAnimalAgeClass] = useState('');
  const [animalAntlerDescription, setAnimalAntlerDescription] = useState('');
  const [animalCount, setAnimalCount] = useState<number>(1);
  const [animalBehavior, setAnimalBehavior] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Create mode: pending files to attach after entry is created
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // Edit mode: existing photos for the entry
  const [existingPhotos, setExistingPhotos] = useState<PhotoRecord[]>([]);
  // Upsell gate state
  const [showPhotoUpsell, setShowPhotoUpsell] = useState(false);
  const [showEnvUpsell, setShowEnvUpsell] = useState(false);
  // Photo attachment error (create mode)
  const [photoAttachError, setPhotoAttachError] = useState<string | null>(null);

  useEffect(() => {
    if (waypointId && entries.length === 0) void loadEntriesByWaypoint(waypointId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypointId]);

  // Load properties and waypoints for the selectors when no waypointId comes from the URL
  useEffect(() => {
    if (waypointIdFromUrl) return;
    void loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypointIdFromUrl]);

  useEffect(() => {
    if (waypointIdFromUrl) return;
    const propertyId = selectedPropertyId || activePropertyId;
    if (propertyId) {
      void loadWaypoints(propertyId);
      // Reset selected location when property changes
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return branch clears state before async path
      setSelectedLocationId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypointIdFromUrl, selectedPropertyId, activePropertyId]);

  // Load existing photos in edit mode
  useEffect(() => {
    if (!isEditMode || !entryId) return;
    getPhotosForEntry(entryId)
      .then(setExistingPhotos)
      .catch((err) => console.error('[LogEntryFormPage] Failed to load photos:', err));
  }, [isEditMode, entryId]);

  const handleDeleteExistingPhoto = useCallback(async (photoId: string) => {
    if (!entryId) return;
    try {
      await deletePhoto(photoId, entryId);
      setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      console.error('[LogEntryFormPage] Failed to delete photo:', err);
    }
  }, [entryId]);

  // Populate form in edit mode once entries load
  useEffect(() => {
    if (!isEditMode || !entryId) return;
    const entry = entries.find((e) => e.id === entryId);
    if (!entry) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
    setDate(entry.date);
    setTime(entry.time ?? '');
    setNotes(entry.notes);
    setTags(entry.tags);
    setAnimalObserved(entry.animalObserved ?? false);
    setAnimalSpecies(entry.animalSpecies ?? 'Whitetail');
    setAnimalSex(entry.animalSex ?? '');
    setAnimalAgeClass(entry.animalAgeClass ?? '');
    setAnimalAntlerDescription(entry.animalAntlerDescription ?? '');
    setAnimalCount(entry.animalCount ?? 1);
    setAnimalBehavior(entry.animalBehavior ?? '');
  }, [entries, entryId, isEditMode]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!waypointId) errs['location'] = 'Please select a location';
    if (!date) errs['date'] = 'Date is required';
    if (!time) errs['time'] = 'Time is required';
    if (animalObserved && !animalSex) errs['animalSex'] = 'Sex is required when animal observed';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    // Find propertyId from location store
    const location = locations.find((l) => l.id === waypointId);

    setIsSaving(true);
    try {
      const payload = {
        waypointId,
        propertyId: location?.propertyId,
        date,
        time,
        notes: notes.trim(),
        tags,
        animalObserved,
        ...(animalObserved && {
          animalSpecies: animalSpecies.trim() || undefined,
          animalSex: animalSex as 'buck' | 'doe' | 'fawn' | 'unknown' | undefined,
          animalAgeClass: animalAgeClass.trim() || undefined,
          animalAntlerDescription:
            animalSex === 'buck' ? animalAntlerDescription.trim() || undefined : undefined,
          animalCount: animalCount || undefined,
          animalBehavior: animalBehavior || undefined,
        }),
      };

      if (isEditMode && entryId) {
        await updateEntry(entryId, payload);
        navigate(`/waypoints/${waypointId}/log/${entryId}`);
      } else {
        const entry = await createEntry(payload);
        // Attach pending photos after entry is created
        if (pendingFiles.length > 0) {
          if (!userId) {
            throw new Error('LogEntryFormPage: cannot save photos — user not authenticated');
          }
          try {
            await addPhotosToEntry(entry.id, userId, pendingFiles);
          } catch (photoErr) {
            console.error('[LogEntryFormPage] Failed to attach photos:', photoErr);
            const msg = photoErr instanceof Error ? photoErr.message : 'Failed to attach photos. Please try again.';
            setPhotoAttachError(msg);
            setIsSaving(false);
            return;
          }
        }
        setPhotoAttachError(null);
        navigate(`/waypoints/${waypointId}/log/${entry.id}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save entry. Please try again.';
      setErrors((prev) => ({ ...prev, submit: message }));
      setIsSaving(false);
    }
  }

  function handleAnimalToggle() {
    const next = !animalObserved;
    setAnimalObserved(next);
    if (!next) {
      setAnimalSex('');
      setAnimalAgeClass('');
      setAnimalAntlerDescription('');
      setAnimalCount(1);
      setAnimalBehavior('');
    }
  }

  const backTo = isEditMode
    ? `/waypoints/${waypointId}/log/${entryId}`
    : `/waypoints/${waypointId}/log`;

  return (
    <div className="flex flex-col gap-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          {isEditMode ? 'Edit Entry' : 'New Entry'}
        </h1>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5"
        data-testid="log-entry-form"
      >
        {/* Property + Location selectors — shown when no waypointId in URL */}
        {!waypointIdFromUrl && (
          <>
            {/* Property selector — only shown when multiple properties exist */}
            {properties.length > 1 && (
              <FieldWrapper>
                <FieldLabel htmlFor="log-property" required>
                  Property
                </FieldLabel>
                <select
                  id="log-property"
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  className={INPUT_CLASS}
                  data-testid="select-property"
                >
                  <option value="">— Select a property —</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </FieldWrapper>
            )}

            <FieldWrapper>
              <FieldLabel htmlFor="log-location" required>
                Location
              </FieldLabel>
              <select
                id="log-location"
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className={INPUT_CLASS}
                data-testid="select-location"
                disabled={properties.length > 1 && !selectedPropertyId}
              >
                <option value="">— Select a location —</option>
                {locations
                  .filter((l) => {
                    const effectivePropertyId = selectedPropertyId || activePropertyId;
                    return !effectivePropertyId || l.propertyId === effectivePropertyId;
                  })
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
              </select>
              {errors['location'] && (
                <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-location">
                  {errors['location']}
                </p>
              )}
            </FieldWrapper>
          </>
        )}

        {/* Date */}
        <FieldWrapper>
          <DateField
            id="log-date"
            label="Date"
            value={date}
            onChange={setDate}
            required
          />
          {errors['date'] && (
            <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-date">
              {errors['date']}
            </p>
          )}
        </FieldWrapper>

        {/* Time */}
        <FieldWrapper>
          <TimeField
            id="log-time"
            label="Time"
            value={time}
            onChange={setTime}
            required
          />
          {errors['time'] && (
            <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-time">
              {errors['time']}
            </p>
          )}
        </FieldWrapper>

        {/* Notes */}
        <FieldWrapper>
          <FieldLabel htmlFor="log-notes">Notes</FieldLabel>
          <textarea
            id="log-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="What did you see? What stood out?"
            className={`${INPUT_CLASS} resize-y`}
            data-testid="input-notes"
          />
        </FieldWrapper>

        {/* Tags */}
        <FieldWrapper>
          <FieldLabel>Tags</FieldLabel>
          <TagSelector selected={tags} onChange={setTags} />
        </FieldWrapper>

        {/* Photos */}
        <FieldWrapper>
          <FieldLabel>Photos</FieldLabel>
          {/* Existing photos always visible (downgrade preservation) */}
          {isEditMode && existingPhotos.length > 0 && (
            <div className="mb-2" data-testid="existing-photos">
              <PhotoGallery
                photos={existingPhotos}
                onDelete={isPro() ? (id) => void handleDeleteExistingPhoto(id) : undefined}
                editable={isPro()}
              />
            </div>
          )}
          {isPro() ? (
            <>
              {/* Create mode: show pending file count */}
              {!isEditMode && pendingFiles.length > 0 && (
                <p className="text-body-xs text-[var(--color-text-muted)] mb-1" data-testid="pending-count">
                  {pendingFiles.length} photo{pendingFiles.length !== 1 ? 's' : ''} selected
                </p>
              )}
              <PhotoPicker
                logEntryId={entryId ?? ''}
                existingCount={isEditMode ? existingPhotos.length : pendingFiles.length}
                onPhotosAdded={(newPhotos) => setExistingPhotos((prev) => [...prev, ...newPhotos])}
                onAddFiles={isEditMode ? undefined : (files) => setPendingFiles((prev) => [...prev, ...files])}
                disabled={isSaving}
                data-testid="photo-picker"
              />
            </>
          ) : (
            /* Starter: locked photo affordance */
            <button
              type="button"
              aria-disabled="true"
              data-testid="photo-picker-locked"
              onClick={() => {
                posthogService.track(UPGRADE_PROMPT_SHOWN, { feature_gate: 'log_entry_photos' });
                setShowPhotoUpsell(true);
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-md border border-dashed border-[var(--color-border)] text-body-sm text-[var(--color-text-muted)] opacity-50 cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Add Photos
            </button>
          )}
        </FieldWrapper>

        {/* Environmental auto-population indicator */}
        <FieldWrapper>
          <FieldLabel>Environmental Conditions</FieldLabel>
          {isPro() ? (
            <p className="text-body-xs text-[var(--color-text-muted)]" data-testid="env-pro-note">
              Weather and moon data will be auto-populated when available.
            </p>
          ) : (
            <button
              type="button"
              data-testid="env-autofill-locked"
              onClick={() => {
                posthogService.track(UPGRADE_PROMPT_SHOWN, { feature_gate: 'scouting_environmental' });
                setShowEnvUpsell(true);
              }}
              className="flex items-center gap-2 text-body-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Pro — Auto-fill weather &amp; moon data
            </button>
          )}
        </FieldWrapper>

        {/* Animal observation toggle */}
        <div
          className={`flex flex-col gap-3 bg-bark-800 rounded-lg p-4 ${
            animalObserved ? 'border-l-2 border-ember-500' : ''
          }`}
          data-observed={animalObserved ? 'true' : 'false'}
          data-testid="animal-container"
        >
          <button
            type="button"
            onClick={handleAnimalToggle}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md border text-body-sm font-medium transition-colors ${
              animalObserved
                ? 'bg-[var(--color-accent-subtle)] border-[var(--color-accent-pressed)] text-[var(--color-accent-hover)]'
                : 'bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
            }`}
            data-testid="animal-toggle"
          >
            <span
              className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${
                animalObserved ? 'bg-[var(--color-accent)] border-[var(--color-accent)]' : 'border-[var(--color-border-subtle)]'
              }`}
            >
              {animalObserved && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M2 5l2.5 2.5L8 3"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            Animal Observed
          </button>

          {animalObserved && (
            <div
              className="flex flex-col gap-4"
              data-testid="animal-section"
            >
              {/* Species */}
              <FieldWrapper>
                <FieldLabel htmlFor="animal-species">Species</FieldLabel>
                <input
                  id="animal-species"
                  type="text"
                  value={animalSpecies}
                  onChange={(e) => setAnimalSpecies(e.target.value)}
                  placeholder="Whitetail"
                  className={INPUT_CLASS}
                  data-testid="input-species"
                />
              </FieldWrapper>

              {/* Sex */}
              <FieldWrapper>
                <FieldLabel htmlFor="animal-sex" required>
                  Sex
                </FieldLabel>
                <select
                  id="animal-sex"
                  value={animalSex}
                  onChange={(e) => setAnimalSex(e.target.value)}
                  className={INPUT_CLASS}
                  data-testid="select-sex"
                >
                  <option value="">— Select sex —</option>
                  {ANIMAL_SEX_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </select>
                {errors['animalSex'] && (
                  <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-animal-sex">
                    {errors['animalSex']}
                  </p>
                )}
              </FieldWrapper>

              {/* Age class */}
              <FieldWrapper>
                <FieldLabel htmlFor="animal-age">Age Class</FieldLabel>
                <input
                  id="animal-age"
                  type="text"
                  value={animalAgeClass}
                  onChange={(e) => setAnimalAgeClass(e.target.value)}
                  placeholder="e.g. 3.5 years, yearling, mature"
                  className={INPUT_CLASS}
                  data-testid="input-age-class"
                />
              </FieldWrapper>

              {/* Antler description — only for bucks */}
              {animalSex === 'buck' && (
                <FieldWrapper>
                  <FieldLabel htmlFor="animal-antler">Antler Description</FieldLabel>
                  <input
                    id="animal-antler"
                    type="text"
                    value={animalAntlerDescription}
                    onChange={(e) => setAnimalAntlerDescription(e.target.value)}
                    placeholder="e.g. 8-point, wide spread, drop tine"
                    className={INPUT_CLASS}
                    data-testid="input-antler"
                  />
                </FieldWrapper>
              )}

              {/* Count */}
              <FieldWrapper>
                <FieldLabel htmlFor="animal-count">Count</FieldLabel>
                <input
                  id="animal-count"
                  type="number"
                  min="1"
                  value={animalCount}
                  onChange={(e) => setAnimalCount(Number(e.target.value))}
                  className={INPUT_CLASS}
                  data-testid="input-count"
                />
              </FieldWrapper>

              {/* Behavior */}
              <FieldWrapper>
                <FieldLabel htmlFor="animal-behavior">Behavior</FieldLabel>
                <select
                  id="animal-behavior"
                  value={animalBehavior}
                  onChange={(e) => setAnimalBehavior(e.target.value)}
                  className={INPUT_CLASS}
                  data-testid="select-behavior"
                >
                  <option value="">— Select behavior —</option>
                  {ANIMAL_BEHAVIOR_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </select>
              </FieldWrapper>
            </div>
          )}
        </div>

        {/* Photo attachment error */}
        {photoAttachError !== null && (
          <InlineErrorBanner
            message={photoAttachError}
            data-testid="photo-attach-error"
          />
        )}

        {/* Submit error */}
        {errors['submit'] && (
          <p
            className="text-body-sm text-[var(--color-accent)] text-center"
            data-testid="error-submit"
          >
            {errors['submit']}
          </p>
        )}

        {/* Submit */}
        <Button
          type="submit"
          variant="primary"
          disabled={isSaving}
          className="w-full mt-2"
          data-testid="submit-button"
        >
          {isSaving ? 'Saving\u2026' : isEditMode ? 'Save Changes' : 'Add Entry'}
        </Button>
      </form>

      {showPhotoUpsell && (
        <UpsellSheet
          featureName={UPSELL_COPY.log_entry_photos.featureName}
          description={UPSELL_COPY.log_entry_photos.description}
          featureGate="log_entry_photos"
          onDismiss={() => setShowPhotoUpsell(false)}
        />
      )}

      {showEnvUpsell && (
        <UpsellSheet
          featureName={UPSELL_COPY.scouting_environmental.featureName}
          description={UPSELL_COPY.scouting_environmental.description}
          featureGate="scouting_environmental"
          onDismiss={() => setShowEnvUpsell(false)}
        />
      )}
    </div>
  );
}
